import { computed, inject, Injectable, signal } from '@angular/core';
import { ThemeTag } from '../../entities';
import { Activity } from '../../entities/activity.type';
import { BrowserStorageService } from '../../../shared/storage/browser-storage.service';
import { deepEqual } from '../../../shared/helpers/deep-equal';

export interface CartActivity extends Activity {
  videoId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  readonly #browserStorageService = inject(BrowserStorageService);
  readonly #storageKey = 'cart';

  // Signal is not updated by default when we add/delete Map keys, because it doesn't mutate Map's reference, that's why we provide deepEqual function to trigger changes
  readonly #activitiesMap = signal<Map<string, CartActivity[]>>(this.#getStoredCart(), { equal: deepEqual });

  #selectedTags = signal<ThemeTag[]>([]);

  selectedActivities = computed<CartActivity[]>(() =>
    Array.from(this.#activitiesMap().values()).reduce((acc, value) => acc.concat(value), []),
  );

  public tags = this.#selectedTags.asReadonly();
  public count = computed(() => this.tags().length + this.selectedActivities().length);
  public isCartVisible = signal(false);

  public addTag(tag: ThemeTag) {
    this.#selectedTags.update((tags) => [...tags, tag]);
  }

  public removeTag(removedTag: ThemeTag) {
    this.#selectedTags.update((tags) => tags.filter((tag) => tag.id === removedTag.id));
  }

  public addActivity(activity: Activity, videoId: string) {
    this.#activitiesMap.update((map) => {
      const mapCopy = new Map(map);

      const currentActivities: Activity[] = mapCopy.get(videoId) ?? [];
      mapCopy.set(videoId, [...currentActivities, { ...activity, videoId }]);

      return mapCopy;
    });
    this.#storeCartInBrowserStorage();
  }

  public addAllVideoActivities(activities: Activity[], videoId: string): void {
    if (!activities.length) return;

    this.#activitiesMap.update((map) => {
      const mapCopy = new Map(map);

      const currentActivities: Activity[] = mapCopy.get(videoId) ?? [];
      const mappedActivities: CartActivity[] = activities.map((activity) => ({ ...activity, videoId }));

      mapCopy.set(videoId, [...currentActivities, ...mappedActivities]);

      return mapCopy;
    });
    this.#storeCartInBrowserStorage();
  }

  public removeActivity(activity: CartActivity, videoId?: string): void {
    const actualVideoId: string | undefined = videoId ?? activity.videoId;
    if (!actualVideoId) return;

    this.#activitiesMap.update((map) => {
      const mapCopy = new Map(map);

      const currentActivities: Activity[] = mapCopy.get(actualVideoId) ?? [];
      const updatedActivities: Activity[] = currentActivities.filter((a) => a.id !== activity.id);

      if (!updatedActivities.length) {
        mapCopy.delete(actualVideoId);
      } else {
        mapCopy.set(actualVideoId, updatedActivities);
      }

      return mapCopy;
    });
    this.#storeCartInBrowserStorage();
  }

  public removeAllActivities(): void {
    this.#activitiesMap.set(new Map());
    this.#browserStorageService.remove('cart');
  }

  public removeAllVideoActivities(videoId: string): void {
    this.#activitiesMap.update((map) => {
      const mapCopy = new Map(map);

      mapCopy.delete(videoId);
      return mapCopy;
    });
    this.#storeCartInBrowserStorage();
  }

  public toggleActivity(activity: CartActivity, videoId: string | undefined): void {
    if (this.isActivityInCart(activity.id)) {
      this.removeActivity(activity, videoId);
    } else {
      if (!videoId) return;

      this.addActivity(activity, videoId);
    }
  }

  public isActivityInCart(activityId: string) {
    return this.selectedActivities().some((activity) => activity.id === activityId);
  }

  public isAtLeastOneVideoActivityInCart(videoId: string): boolean {
    return this.#activitiesMap().has(videoId);
  }

  public getTagsForFilter(filter: 'all' | 'location' | 'activity', tags: ThemeTag[]): ThemeTag[] {
    if (!tags) return [];

    if (filter === 'all') return tags.map((tag) => ({ ...tag, count: 1 }));

    return tags
      .filter((tag) => tag.category === filter)
      .map((tag) => ({
        ...tag,
        count: 1,
      }));
  }

  #storeCartInBrowserStorage(): void {
    const entriesArray: [string, CartActivity[]][] = Array.from(this.#activitiesMap().entries());
    this.#browserStorageService.set(this.#storageKey, JSON.stringify(entriesArray));
  }

  #getStoredCart(): Map<string, CartActivity[]> {
    let map = new Map<string, CartActivity[]>();
    const cartRecord = this.#browserStorageService.get(this.#storageKey);

    if (cartRecord) {
      const parsedCartEntries: [string, CartActivity[]][] = JSON.parse(cartRecord);
      map = new Map(parsedCartEntries);
    }

    return map;
  }
}
